<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';

// Smith3D alert bar (owner 2026-09-22, replaces the floating bubble): sits with
// the chat-list search box — the one place staff eyes pass on every filtered
// view — and is sized entirely by the embedded page via boardHeight messages
// (0 = hidden until the next alert; 40 = ticker; taller = expanded panel).
// All bar behaviour (hide-until-new, severity colors, marquee, SSE push) lives
// server-side in mk-backend-s3 so iterating on it needs no Chatwoot rebuild.
// Gated by smith3d TEAM membership, independent of the current view.
const S3_BOARD_ORIGIN =
  window.location.hostname === 'localhost'
    ? 'http://localhost:3669'
    : 'https://sapi3.mkhb.my';

const route = useRoute();
const router = useRouter();
const teams = useMapGetter('teams/getMyTeams');

const isSmithStaff = computed(() =>
  teams.value.some(team => team.name.toLowerCase() === 'smith3d')
);

const barHeight = ref(0);

const barSrc = computed(() => {
  const theme = document.body.classList.contains('dark') ? 'dark' : 'light';
  return `${S3_BOARD_ORIGIN}/api/store-dashboard/bar?theme=${theme}`;
});

const onBoardMessage = event => {
  if (event.origin !== S3_BOARD_ORIGIN) return;
  let data = event.data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return;
    }
  }
  if (data?.event === 'boardHeight') {
    barHeight.value = Number(data.px) || 0;
    return;
  }
  // Label-count chips navigate the SPA in place (e.g. /app/.../label/x).
  if (data?.event === 'boardGoto') {
    if (typeof data.path === 'string' && data.path.startsWith('/app/')) {
      router.push(data.path);
    }
    return;
  }
  if (data?.event !== 'boardNavigate' || !data.conversationId) return;
  router.push({
    name: 'conversations_through_company',
    params: {
      accountId: route.params.accountId,
      company: 's3',
      conversation_id: data.conversationId,
    },
  });
};

onMounted(() => window.addEventListener('message', onBoardMessage));
onBeforeUnmount(() => window.removeEventListener('message', onBoardMessage));
</script>

<template>
  <div
    v-if="isSmithStaff"
    class="w-full overflow-hidden transition-all duration-300 shrink-0"
    :style="{ height: `${barHeight}px` }"
  >
    <iframe
      :src="barSrc"
      class="w-full h-full border-0"
      :title="$t('CHAT_LIST.S3_BOARD')"
    />
  </div>
</template>
